import {Add} from '@mui/icons-material'
import {Box, Button, Paper, Stack} from '@mui/material'
import {Dispatch, SetStateAction, useEffect} from 'react'
import {FormProvider, useFieldArray, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {useUpdateAssetConfig} from '@kilnfeature/api/mutations'
import {HeatZonesTable} from '@kilnfeature/modules/heatZones/HeatZonesForm/HeatZonesTable'
import {AssetConfigurationDto, HeatZone} from '@kilnfeature/types'

type HeatZonesFormProps = {
  heatZones: HeatZone[]
  kilnLength: number
  setHeatZones: Dispatch<SetStateAction<HeatZone[]>>
  assetConfig: AssetConfigurationDto
}

export const HeatZonesForm = ({
  heatZones,
  kilnLength,
  setHeatZones,
  assetConfig
}: HeatZonesFormProps) => {
  const {t} = useTranslation()
  const {mutate: updateAssetConfig, isPending: isConfigUpdating} = useUpdateAssetConfig()
  const methods = useForm<{heatZones: HeatZone[]}>({defaultValues: {heatZones}})
  const {control, watch, setValue, handleSubmit} = methods
  const {fields, append, remove} = useFieldArray({
    control,
    name: 'heatZones'
  })

  useEffect(() => {
    const subscription = watch((value) => {
      if (value.heatZones) {
        setHeatZones(
          value.heatZones.map((z) => ({
            name: z?.name ?? '',
            start: z?.start ?? 0,
            end: z?.end ?? 0
          }))
        )
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, setHeatZones])

  const addHeatZone = () => {
    const lastZoneIndex = heatZones.length - 1
    const zone = heatZones[lastZoneIndex]
    setValue(`heatZones.${lastZoneIndex}.end`, zone.end - 1)
    append({
      name: '',
      start: kilnLength - 1,
      end: kilnLength
    })
  }

  const fixMeterType = (value: string | number) => {
    return Number(Number(value).toFixed(2))
  }

  const handleFormSubmit = (data: {heatZones: HeatZone[]}) => {
    updateAssetConfig({
      ...assetConfig,
      payload: {
        ...assetConfig.payload,
        layout: {
          ...assetConfig.payload.layout,
          heatZones: data.heatZones.map((zone) => ({
            name: zone.name,
            start: fixMeterType(zone.start),
            end: fixMeterType(zone.end)
          }))
        },
        configuration: {
          ...assetConfig.payload.configuration,
          heatZones: data.heatZones.map((zone) => ({
            name: zone.name,
            start: fixMeterType(zone.start),
            end: fixMeterType(zone.end)
          }))
        }
      }
    })
  }

  return (
    <Box>
      <FormProvider {...methods}>
        <Stack
          component={'form'}
          onSubmit={handleSubmit(handleFormSubmit)}
          data-test-id={'heat-zones-form'}
          spacing={4}
        >
          <Stack gap={2} display={'flex'} justifyContent={'flex-end'} direction={'row'}>
            <Button
              variant={'outlined'}
              color={'secondary'}
              startIcon={<Add />}
              onClick={() => addHeatZone()}
              data-test-id="add_heat_zone_button"
            >
              {t('heatZones.add')}
            </Button>
            <Button
              type={'submit'}
              variant={'contained'}
              disabled={isConfigUpdating}
              data-test-id="save_heat_zones_button"
            >
              {t('common.save')}
            </Button>
          </Stack>
          <Paper>
            <HeatZonesTable fields={fields} remove={remove} kilnLength={kilnLength} />
          </Paper>
        </Stack>
      </FormProvider>
    </Box>
  )
}
